import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import type {
  FacturaCompraPrecargaDesdePdfResult,
} from '../../services/facturaCompraService';
import type { Proveedor } from '../../services/proveedorService';
import NuevoProveedorDesdePrecargaDialog from './NuevoProveedorDesdePrecargaDialog';
import {
  buildFilasEncabezadoPrecarga,
  buildFilaObservacionesPrecarga,
  buildProveedorInicialDesdePrecarga,
  debeSugerirCrearProveedor,
  labelProveedorPrecarga,
  MSG_CAMPO_NO_DETECTADO,
  precargaUtilizoIa,
} from '../../utils/compraFacturaPrecarga';

/** Tras este tiempo, si la precarga sigue en curso, se asume uso de IA (llamada más lenta). */
const MS_ANTES_MENSAJE_IA = 1500;

const AVISO_COSTOS_IA =
  'El uso de inteligencia artificial para complementar datos, puede generar costos adicionales según el proveedor del servicio.';

interface PrecargaFacturaCompraPdfDialogProps {
  open: boolean;
  loading?: boolean;
  precarga: FacturaCompraPrecargaDesdePdfResult | null;
  onClose: () => void;
  onApply: () => void;
  onProveedorCreado?: (proveedor: Proveedor) => void;
}

const renderDescripcionConNotas = (
  descripcion?: string | null,
  notas?: string | null,
  advertencia?: string | null,
) => {
  const desc = descripcion?.trim() || '—';
  const notasTexto = notas?.trim();
  const advertenciaTexto = advertencia?.trim();

  return (
    <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      <Typography variant="body2">{desc}</Typography>
      {notasTexto ? (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          <strong>Notas:</strong> {notasTexto}
        </Typography>
      ) : null}
      {advertenciaTexto ? (
        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
          <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 16, mt: 0.15, flexShrink: 0 }} />
          <Typography variant="caption" color="warning.main" component="div">
            {advertenciaTexto}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
};

const renderValorCampoEncabezado = (valor: string | null, noDetectado: boolean) => {
  if (noDetectado) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 16, mt: 0.15, flexShrink: 0 }} />
        <Typography variant="body2" color="warning.main" component="div">
          {MSG_CAMPO_NO_DETECTADO}
        </Typography>
      </Stack>
    );
  }

  return valor ?? '—';
};

const PrecargaFacturaCompraPdfDialog: React.FC<PrecargaFacturaCompraPdfDialogProps> = ({
  open,
  loading = false,
  precarga,
  onClose,
  onApply,
  onProveedorCreado,
}) => {
  const [faseIa, setFaseIa] = useState(false);
  const [nuevoProveedorOpen, setNuevoProveedorOpen] = useState(false);

  useEffect(() => {
    if (!loading) {
      setFaseIa(false);
      return undefined;
    }

    setFaseIa(false);
    const timerId = window.setTimeout(() => setFaseIa(true), MS_ANTES_MENSAJE_IA);
    return () => window.clearTimeout(timerId);
  }, [loading]);

  const advertencias: string[] = precarga?.advertencias ?? [];
  const itemsProductos = precarga?.itemsProductos ?? [];
  const itemsConceptos = precarga?.itemsConceptos ?? [];
  const filasEncabezado = precarga ? buildFilasEncabezadoPrecarga(precarga) : [];
  const filaObservaciones = precarga ? buildFilaObservacionesPrecarga(precarga) : null;
  const totalLineas = itemsProductos.length + itemsConceptos.length;
  const tieneDatosUtiles =
    totalLineas > 0 ||
    filasEncabezado.some((f) => !f.noDetectado) ||
    Boolean(filaObservaciones && !filaObservaciones.noDetectado);
  const puedeAplicar = Boolean(precarga && tieneDatosUtiles);
  const utilizoIa = precargaUtilizoIa(precarga);
  const sugerirCrearProveedor = debeSugerirCrearProveedor(precarga);
  const proveedorVinculado = Boolean(precarga?.proveedor.encontrado);

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Precarga desde PDF</DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 2 }}>
            <CircularProgress size={20} sx={{ mt: 0.25, flexShrink: 0 }} />
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {faseIa ? 'Complementando con inteligencia artificial…' : 'Analizando el PDF…'}
              </Typography>
              {faseIa ? (
                <Typography variant="caption" color="text.secondary">
                  {AVISO_COSTOS_IA}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        ) : precarga ? (
          <Stack spacing={2}>
            {proveedorVinculado && (
              <Alert severity="success" variant="outlined">
                Proveedor: <strong>{labelProveedorPrecarga(precarga)}</strong>
              </Alert>
            )}

            {sugerirCrearProveedor && (
              <Alert
                severity="info"
                variant="outlined"
                action={
                  <Button color="inherit" size="small" onClick={() => setNuevoProveedorOpen(true)}>
                    Crear proveedor
                  </Button>
                }
              >
                El proveedor no está registrado: <strong>{labelProveedorPrecarga(precarga)}</strong>.
                Podés darlo de alta con los datos detectados del PDF.
              </Alert>
            )}

            {utilizoIa && (
              <Alert severity="info" variant="outlined">
                Parte de los datos fueron interpretados con inteligencia artificial. {AVISO_COSTOS_IA}
              </Alert>
            )}

            {advertencias.length > 0 && (
              <Stack spacing={1}>
                {advertencias.map((adv, i) => (
                  <Alert key={i} severity="warning" variant="outlined">
                    {adv}
                  </Alert>
                ))}
              </Stack>
            )}

            {totalLineas > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Detalle detectado: <strong>{itemsProductos.length}</strong> producto
                  {itemsProductos.length === 1 ? '' : 's'}, <strong>{itemsConceptos.length}</strong> concepto
                  {itemsConceptos.length === 1 ? '' : 's'}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Código</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Cant.</TableCell>
                        <TableCell align="right">Costo unit.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemsProductos.map((it, idx) => (
                        <TableRow key={`p-${idx}`}>
                          <TableCell>Producto</TableCell>
                          <TableCell>{it.codigoArticulo}</TableCell>
                          <TableCell>{renderDescripcionConNotas(it.descripcion, it.notas)}</TableCell>
                          <TableCell align="right">{it.cantidad}</TableCell>
                          <TableCell align="right">{it.costoBrutoUnitario}</TableCell>
                        </TableRow>
                      ))}
                      {itemsConceptos.map((it, idx) => (
                        <TableRow key={`c-${idx}`}>
                          <TableCell>Concepto</TableCell>
                          <TableCell>{it.nroCuenta ?? it.codigoArticulo}</TableCell>
                          <TableCell>{renderDescripcionConNotas(it.descripcion, it.notas, it.advertencia)}</TableCell>
                          <TableCell align="right">{it.cantidad}</TableCell>
                          <TableCell align="right">{it.costoBrutoUnitario}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campo</TableCell>
                    <TableCell>Valor detectado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filasEncabezado.map((fila) => (
                    <TableRow key={fila.nombre}>
                      <TableCell>{fila.label}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {renderValorCampoEncabezado(fila.valor, fila.noDetectado)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filaObservaciones ? (
                    <TableRow key={filaObservaciones.nombre}>
                      <TableCell>{filaObservaciones.label}</TableCell>
                      <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {renderValorCampoEncabezado(filaObservaciones.valor, filaObservaciones.noDetectado)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        ) : null}
      </DialogContent>
      {(loading || precarga) ? (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            flexShrink: 0,
          }}
        >
          {!loading && precarga ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Revise los datos antes de aplicar. Compañía y ubicación destino deben completarse manualmente.
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.4 }}>
            Los valores se identifican automáticamente a partir del PDF (reglas e inteligencia artificial) y pueden contener errores.
            Verifique cada dato antes de aplicar la precarga.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.4, mt: 0.5 }}
          >
            {AVISO_COSTOS_IA}
          </Typography>
        </Box>
      ) : null}
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onApply}
          disabled={loading || !puedeAplicar}
        >
          Aplicar precarga
        </Button>
      </DialogActions>
    </Dialog>

    {precarga && (
      <NuevoProveedorDesdePrecargaDialog
        open={nuevoProveedorOpen}
        initialData={buildProveedorInicialDesdePrecarga(precarga)}
        onClose={() => setNuevoProveedorOpen(false)}
        onCreated={(proveedor) => {
          setNuevoProveedorOpen(false);
          onProveedorCreado?.(proveedor);
        }}
      />
    )}
    </>
  );
};

export default PrecargaFacturaCompraPdfDialog;
